import { IconInfoCircle, IconRibbonStar } from '@justeattakeaway/pie-icons-react';
import PIECard from '@src/shared/components/PIECard';
import PIEDivider from '@src/shared/components/PIEDivider';
import PIETable from '@src/shared/components/PIETable';
import { ETagsColorType, PIETag } from '@src/shared/components/PIETag';
import PIEText from '@src/shared/components/PIEText';
import { useKeycloakUserState } from '@src/shared/context/keycloak-user';
import useFMFeatureFlag from '@src/shared/hooks/use-fm-featureflag';
import { JETFM_TIPS_RESTRICTED_COUNTRY_LIST } from '@src/shared/models';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './DashboardDeliveryDatacourierTableListItem.module.scss';
import PIETooltip from '@src/shared/components/PIETooltip';
import useDeviceDetect from '@src/shared/hooks/use-device-detect';
import DeliveryDashboardBottomSheet from '../../../DeliveryDashboardBottomSheet';
import { PIEComponentUtil } from '@src/shared/components/PIEComponentUtil';
import PIEButton from '@src/shared/components/PIEButton';

interface componentProps {
  isMostDelivered?: boolean;
  courierName: string;
  deliveredCount: number;
  cashCollected: string;
  tipCollected: string;
  courierType: 'REGISTERED' | 'GUEST';
}

const DashboardDeliveryDataCourierTableListItem = ({
  isMostDelivered,
  courierName,
  deliveredCount,
  cashCollected,
  tipCollected,
  courierType,
}: componentProps) => {
  const { t } = useTranslation();
  const tipsRestrictedCountryList = useFMFeatureFlag(JETFM_TIPS_RESTRICTED_COUNTRY_LIST);
  const { restaurant } = useKeycloakUserState();
  const isTipsRestrictedCountry = tipsRestrictedCountryList.includes(restaurant?.country || '');
  const { isResolutionMobile } = useDeviceDetect();
  const [showBottomSheet, setShowBottomSheet] = React.useState(false);

  const getHeaderColumns = () => {
    const baseColumns = [
      <PIEText
        text={t('deliverydata.COURIER_TABLE_HEADER_DELIVERED')}
        color="var(--xds-color-content-default)"
        size="var(--xds-font-size-16)"
        fontWeight="var(--xds-font-weight-regular)"
      />,
      <PIEText
        text={t('deliverydata.COURIER_TABLE_HEADER_CASH')}
        color="var(--xds-color-content-default)"
        size="var(--xds-font-size-16)"
        fontWeight="var(--xds-font-weight-regular)"
      />,
    ];

    if (!isTipsRestrictedCountry) {
      baseColumns.push(
        <PIEText
          text={t('deliverydata.COURIER_TABLE_HEADER_TIPS')}
          color="var(--xds-color-content-default)"
          size="var(--xds-font-size-16)"
          fontWeight="var(--xds-font-weight-regular)"
        />
      );
    }

    return baseColumns;
  };

  const getDataColumns = () => {
    const deliveredColumn = isMostDelivered ? (
      <PIETag mode={ETagsColorType.Brand05}>
        <IconRibbonStar size="xs" />{' '}
        <PIEText
          text={deliveredCount}
          color="var(--xds-color-content-default)"
          size="var(--xds-font-size-14)"
          fontWeight="var(--xds-font-weight-bold)"
        />
      </PIETag>
    ) : (
      <PIEText
        text={deliveredCount}
        color="var(--xds-color-content-default)"
        size="var(--xds-font-size-16)"
        fontWeight="var(--xds-font-weight-bold)"
      />
    );

    const baseColumns = [
      deliveredColumn,
      <PIEText
        text={cashCollected}
        color="var(--xds-color-content-default)"
        size="var(--xds-font-size-16)"
        fontWeight="var(--xds-font-weight-bold)"
      />,
    ];

    if (!isTipsRestrictedCountry) {
      baseColumns.push(
        <PIEText
          text={tipCollected}
          color="var(--xds-color-content-default)"
          size="var(--xds-font-size-16)"
          fontWeight="var(--xds-font-weight-bold)"
        />
      );
    }

    return baseColumns;
  };

  const renderTooltip = () => {
    if (courierType !== 'REGISTERED') {
      if (!isResolutionMobile) {
        return (
          <PIETooltip position="top" align="end" toggle={<IconInfoCircle size="xs" />}>
            {t('deliverydata.GUEST_COURIER_INFO_TEXT')}
          </PIETooltip>
        );
      } else {
        return (
          <>
            <IconInfoCircle size="xs" onClick={() => setShowBottomSheet(true)} />
            {showBottomSheet && (
              <DeliveryDashboardBottomSheet
                className={styles.guestCourierInfoBottomSheet}
                skipInitialTransition={true}
                header={
                  <div style={{ top: '16px', position: 'absolute' }}>
                    <PIEText
                      text={
                        <>
                          <IconInfoCircle size="m" /> {t('deliverydata.GUEST_COURIER_INFO_TITLE')}
                        </>
                      }
                      color="var(--xds-color-content-default)"
                      size="var(--xds-font-size-20)"
                      fontWeight="var(--xds-font-weight-bold)"
                      align="left"
                    />
                  </div>
                }
                isDetailView={false}
                showCloseButton={true}
                constantHeight={210}
                onBottomSheetHeight={() => {}}
                blocking={true}
                onClose={() => setShowBottomSheet(false)}
              >
                <PIEComponentUtil
                  marginX={'var(--xds-spacing-d)'}
                  className={styles.guestCourierInfoBottomSheetContent}
                >
                  <PIEText
                    text={t('deliverydata.GUEST_COURIER_INFO_TEXT')}
                    color="var(--xds-color-content-default)"
                    size="var(--xds-font-size-16)"
                    fontWeight="var(--xds-font-weight-regular)"
                    align="left"
                  />
                  <PIEComponentUtil marginTop="var(--xds-spacing-e)">
                    <PIEButton
                      type="submit"
                      mode="primary"
                      size="medium"
                      text={t('deliverydata.GUEST_COURIER_INFO_BUTTON')}
                      autoWidth={true}
                      onClick={() => setShowBottomSheet(false)}
                    />
                  </PIEComponentUtil>
                </PIEComponentUtil>
              </DeliveryDashboardBottomSheet>
            )}
          </>
        );
      }
    }
  };

  return (
    <div>
      <PIECard className={styles.deliverydataCourierItemContainer}>
        <PIEText
          text={
            <div className={styles.courierNameContainer}>
              {courierName} {renderTooltip()}
            </div>
          }
          color="var(--xds-color-content-default)"
          size="var(--xds-font-size-16)"
          fontWeight="var(--xds-font-weight-bold)"
        />

        <PIEDivider />

        <PIETable data={[getHeaderColumns(), getDataColumns()]} />
      </PIECard>
    </div>
  );
};

export default DashboardDeliveryDataCourierTableListItem;
